import saveAs from 'file-saver';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';
import { useCurrentCompany, useToast } from 'utils/hooks';

const base64ToBlob = (b64Data: string, contentType = '', sliceSize = 512) => {
  try {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  } catch (e) {
    console.error('atob failed for base64 data:', b64Data.substring(0, 100) + '...');
    console.error('Error details:', e);
    throw e;
  }
};

export const usePortfolioBulkActions = () => {
  const { company } = useCurrentCompany();
  const toast = useToast();
  const nhost = useNhostClient();

  const downloadTemplate = async (
    reportingPeriod = 2024,
    activityRefs: string[] = ['transport-hazardous'],
    selectedCompanies: string[]
  ) => {
    if (!company) throw new Error('No company');

    try {
      const result = await nhost.functions.post('/bulk/portfolios/download-template', {
        ownerCompanyId: company?.id,
        reportingPeriod,
        activityRefs,
        selectedCompanies,
        currency: company?.currency,
      });

      let blob: Blob;
      if (result.body instanceof Blob) {
        // If somehow Blob is returned, use it (though it might be corrupted)
        blob = result.body;
      } else if (typeof result.body === 'string') {
        // Decode base64 string to Blob
        blob = base64ToBlob(result.body);
      } else {
        throw new Error(`Unexpected response body type: ${typeof result.body}`);
      }

      saveAs(blob, `${company?.name}-portfolio-template.xlsx`);
    } catch (e) {
      const error = e as Error;
      toast({
        text: error.message || 'Something went wrong, please try again',
        variant: 'danger',
      });
    }
  };

  const uploadTemplate = async (file: File) => {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const payload = {
      file: {
        name: file.name,
        type: file.type,
        size: file.size,
        data: base64,
      },
      bucketId: 'default',
    };

    const response = await nhost.functions.post('/upload-file', payload);

    const data = response.body as { fileId: string; fileName: string };

    return nhost.functions.post('/bulk/portfolios/upload-template', {
      fileId: data.fileId,
    });
  };

  return {
    downloadTemplate,
    uploadTemplate,
  };
};

export const usePaiBulkActions = () => {
  const { company } = useCurrentCompany();
  const toast = useToast();
  const nhost = useNhostClient();
  const downloadTemplate = async (reportingPeriod = 2024) => {
    if (!company) throw new Error('No company');

    try {
      const result = await nhost.functions.post('/bulk/portfolios/pai/download-pai-template', {
        reportingPeriod,
        currency: company?.currency,
        ownerCompanyId: company?.id,
      });

      let blob: Blob;
      if (result.body instanceof Blob) {
        // If somehow Blob is returned, use it (though it might be corrupted)
        blob = result.body;
      } else if (typeof result.body === 'string') {
        // Decode base64 string to Blob
        blob = base64ToBlob(result.body);
      } else {
        throw new Error(`Unexpected response body type: ${typeof result.body}`);
      }

      saveAs(blob, `${company?.name}-pai-template.xlsx`);
    } catch (e: unknown) {
      const error = e as Error;
      toast({
        text: error.message || 'Something went wrong, please try again',
        variant: 'danger',
      });
    }
  };

  const uploadTemplate = async (file: File) => {
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const payload = {
      file: {
        name: file.name,
        type: file.type,
        size: file.size,
        data: base64,
      },
      bucketId: 'default',
    };

    const response = await nhost.functions.post('/upload-file', payload);

    const data = response.body as { fileId: string; fileName: string };

    return nhost.functions.post('/bulk/portfolios/pai/upload-pai-template', {
      fileId: data.fileId,
    });
  };

  return {
    downloadTemplate,
    uploadTemplate,
  };
};
